import { ColumnConfig, numberSort, SortDirections, Waffles, wafflesKeyColumnName } from "@app/shared/waffles";
import { JSX, useEffect, useMemo, useState } from "react";
import { QESAlert } from "../shared/qes-standards";
import { Version } from "./memberSetModels";
import MemberSetService from "./memberSetService";
import { getComparisonChangeElement } from "@app/shared/utility";

interface MemberSetCountByCountyProps {
  selectedVersion: Version;
  comparedVersion: Version;
  compareMode: boolean;
}

interface MemberSetCountByCountyTableData {
  [wafflesKeyColumnName]: string;
  comparedMemberCount?: string;
  comparedNumericMemberCount?: number;
  change?: JSX.Element;
  changeNumeric?: number;
  changePercent?: JSX.Element;
  changePercentNumeric?: number;
  countyName: string;
  countySSA: string;
  memberCount?: string;
  numericMemberCount?: number;
  stateAbbreviation: string;
}

const MemberSetCountByCountyTable = (props: MemberSetCountByCountyProps): JSX.Element => {
  const loadMoreIncrement = 100;
  const memberSetService = new MemberSetService();
  const compareMode = props.compareMode;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<JSX.Element>(null);
  const [countyMemberCountModels, setCountyMemberCountModels] = useState<MemberSetCountByCountyTableData[]>([]);
  const [comparedCountyMemberCountModels, setComparedCountyMemberCountModels] = useState<MemberSetCountByCountyTableData[]>([]);
  const [numberOfDisplayedRecords, setNumberOfDisplayedRecords] = useState(loadMoreIncrement);

  const handleLoadMore = (justChecking: boolean) => {
    if (justChecking) {
      return !(numberOfDisplayedRecords >= countyMemberCountModels.length && (comparedCountyMemberCountModels ? numberOfDisplayedRecords > comparedCountyMemberCountModels.length : true));
    }
    const newNumberOfDisplayedRecords = numberOfDisplayedRecords + loadMoreIncrement;
    setNumberOfDisplayedRecords(newNumberOfDisplayedRecords);
    return !(newNumberOfDisplayedRecords >= countyMemberCountModels.length && (comparedCountyMemberCountModels ? numberOfDisplayedRecords > comparedCountyMemberCountModels.length : true));
  };

  const getColumns = useMemo(() => {
    const compared = props.compareMode;
    const memberSetCountByCountyColumnConfig: ColumnConfig = {
      change: {
        displayText: "Change",
        hidden: !compared,
        testId: "change",
      },
      changePercent: {
        displayText: "% Change",
        hidden: !compared,
        testId: "change-percent",
      },
      comparedMemberCount: {
        displayText: props.compareMode && props.comparedVersion ? new Date(props.comparedVersion.dateCreated).toLocaleString("en-US") : "",
        hidden: !compared,
        testId: "compared-count",
      },
      countyName: {
        displayText: "County",
        testId: "county",
      },
      countySSA: {
        displayText: "County SSA",
        testId: "county-ssa",
      },
      memberCount: {
        displayText: compared ? new Date(props.selectedVersion.dateCreated).toLocaleString("en-US") : "Count",
        testId: "count",
      },
    };

    return memberSetCountByCountyColumnConfig;
  }, [props.compareMode, props.comparedVersion, props.selectedVersion]);

  const getData = useMemo(() => {
    const data = compareMode && comparedCountyMemberCountModels ? comparedCountyMemberCountModels.map((value) => {
      const location = countyMemberCountModels.findIndex((obj) => {
        return obj.countySSA === value.countySSA;
      });
      /* eslint-disable sort-keys */
      if (location !== -1) {
        const difference = countyMemberCountModels[location].numericMemberCount - value.comparedNumericMemberCount;
        return {
          [wafflesKeyColumnName]: countyMemberCountModels[location][wafflesKeyColumnName],
          stateAbbreviation: countyMemberCountModels[location].stateAbbreviation,
          countyName: countyMemberCountModels[location].countyName,
          countySSA: countyMemberCountModels[location].countySSA,
          comparedMemberCount: value.comparedMemberCount,
          comparedNumericMemberCount: value.comparedNumericMemberCount,
          memberCount: countyMemberCountModels[location].memberCount,
          numericMemberCount: countyMemberCountModels[location].numericMemberCount,
          change: getComparisonChangeElement(difference),
          changeNumeric: difference,
          changePercent: getComparisonChangeElement(((difference / value.comparedNumericMemberCount) * 100), (num: number) => num.toFixed(1)),
          changePercentNumeric: ((difference / value.comparedNumericMemberCount) * 100),
        };
      } else {
        //if it's not in the source object
        return {
          ...value,
          memberCount: "0",
          numericMemberCount: 0,
          change: getComparisonChangeElement(0 - value.comparedNumericMemberCount),
          changeNumeric: 0 - value.comparedNumericMemberCount,
          changePercent: getComparisonChangeElement(-100, (num: number) => num.toFixed(1)),
          changePercentNumeric: -100,
        };
      }
    }) : countyMemberCountModels;
    /* eslint-enable sort-keys */
    //return data.slice(0, numberOfDisplayedRecords);
    return data;
  }, [comparedCountyMemberCountModels, countyMemberCountModels, compareMode]);

  const customSort = (records: MemberSetCountByCountyTableData[], column: keyof MemberSetCountByCountyTableData, sortDirection: SortDirections) => {
    let recordsToSort: MemberSetCountByCountyTableData[] = records;
    // The records param will be undefined if the load more button is shown, since Waffles won't have all the necessary data to sort
    if (recordsToSort === undefined) {
      recordsToSort = getData;
    }
    let newRecords = [...recordsToSort];
    const multiplier = sortDirection === SortDirections.Asc ? 1 : -1;

    switch (column) {
      case "countyName":
        newRecords = newRecords.sort((a, b) => {
          if (a.stateAbbreviation === b.stateAbbreviation) {
            return (a.countyName > b.countyName ? 1 : -1) * multiplier;
          }
          return (a.stateAbbreviation > b.stateAbbreviation ? 1 : -1) * multiplier;
        });
        break;
      case "countySSA":
        newRecords = newRecords.sort((a, b) => (a[column] > b[column] ? 1 : -1) * multiplier);
        break;
      case "memberCount":
        newRecords = numberSort(newRecords, "numericMemberCount", multiplier);
        break;
      case "comparedMemberCount":
        newRecords = numberSort(newRecords, "comparedNumericMemberCount", multiplier);
        break;
      case "change":
        newRecords = numberSort(newRecords, "changeNumeric", multiplier);
        break;
      case "changePercent":
        newRecords = numberSort(newRecords, "changePercentNumeric", multiplier);
        break;
      default:
        console.warn(`No sorting definition for ${column}.`);
    }

    //set the models to the sorted records
    return newRecords;
  };

  useEffect(() => {
    setLoading(true);

    /* eslint-disable sort-keys */
    memberSetService.getMemberCountByCounty(props.selectedVersion.memberSetId, props.selectedVersion.memberSetVersionId).then((result) => {
      setCountyMemberCountModels(result.map((countyMemberCountModel) => {
        return {
          [wafflesKeyColumnName]: countyMemberCountModel.countySSA,
          countyName: countyMemberCountModel.countyName,
          countySSA: countyMemberCountModel.countySSA,
          memberCount: countyMemberCountModel.memberCount.toLocaleString(),
          numericMemberCount: countyMemberCountModel.memberCount,
          stateAbbreviation: countyMemberCountModel.stateAbbreviation,
        };
      }));
      if (!props.comparedVersion) {
        setLoading(false);
      }
    }, () => {
      setError(<QESAlert type="danger">Something went wrong on our end.</QESAlert>);
    });

    props.comparedVersion ?
      memberSetService.getMemberCountByCounty(props.comparedVersion.memberSetId, props.comparedVersion.memberSetVersionId).then((result) => {
        setComparedCountyMemberCountModels(result.map((countyMemberCountModel) => {
          return {
            [wafflesKeyColumnName]: countyMemberCountModel.countySSA,
            countyName: countyMemberCountModel.countyName,
            countySSA: countyMemberCountModel.countySSA,
            stateAbbreviation: countyMemberCountModel.stateAbbreviation,
            comparedMemberCount: countyMemberCountModel.memberCount.toLocaleString(),
            comparedNumericMemberCount: countyMemberCountModel.memberCount,
          };
        }));
        setLoading(false);
      }, () => {
        setError(<QESAlert type="danger">Something went wrong on our end.</QESAlert>);
      }) : setComparedCountyMemberCountModels(null);

    /* eslint-enable sort-keys */
  }, [props.comparedVersion, props.selectedVersion]);

  if (error) {
    return error;
  }

  return (
    <>
      <small>Displaying {Math.min(numberOfDisplayedRecords, comparedCountyMemberCountModels ? comparedCountyMemberCountModels.length : countyMemberCountModels.length)} of {comparedCountyMemberCountModels ? comparedCountyMemberCountModels.length : countyMemberCountModels.length} rows</small>
      <Waffles<MemberSetCountByCountyTableData>
        columns={getColumns}
        customSort={customSort}
        data={getData.slice(0, numberOfDisplayedRecords)}
        fullWidth={true}
        loading={loading}
        loadMore={handleLoadMore}
        testId="member-count-by-county"
      />
    </>
  );
};

export default MemberSetCountByCountyTable;
